'use client';

import { CloseButton, Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Button, ControlledInput, Label } from 'components';
import { InvoiceSortAttributeEnum, SortOrderEnum } from 'gql/graphql';
import { CloseIcon, FiltersIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { KeysEnum } from 'types/KeysEnum';
import { cn } from 'utils/cn';
import { useInvoicesContext } from '../_context/InvoicesContext';

export default function FilterSortInvoices() {
  const t = useTranslations();

  const { control, handleClear } = useInvoicesContext();

  const optionsSortAttribute = [
    {
      label: t(`invoices.filters.sort_attributes.${InvoiceSortAttributeEnum.Id}` as any),
      value: InvoiceSortAttributeEnum.Id
    },
    {
      label: t(`invoices.filters.sort_attributes.${InvoiceSortAttributeEnum.PoCode}` as any),
      value: InvoiceSortAttributeEnum.PoCode
    },
    {
      label: t(`invoices.filters.sort_attributes.${InvoiceSortAttributeEnum.CreatedAt}` as any),
      value: InvoiceSortAttributeEnum.CreatedAt
    },
    {
      label: t(`invoices.filters.sort_attributes.${InvoiceSortAttributeEnum.Amount}` as any),
      value: InvoiceSortAttributeEnum.Amount
    }
  ];

  const optionsSortOrder = [
    {
      label: t(`filters.sort_order.${SortOrderEnum.Asc}` as any),
      value: SortOrderEnum.Asc
    },
    {
      label: t(`filters.sort_order.${SortOrderEnum.Desc}` as any),
      value: SortOrderEnum.Desc
    }
  ];

  return (
    <div className="flex flex-1 justify-end gap-4">
      <Popover className={'relative'}>
        <PopoverButton as="div" className="outline-none">
          <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.sorting')}>
            <Button
              variant="icon"
              color="none"
              colorIcon="blue-dark"
              startAdornment={<FiltersIcon className="scale-[0.9] !text-tertiary-200" />}
              className="!h-10 w-10 !rounded-3xl !border-1 !border-primary-blue-100 !px-[6px] !outline-none"
            />
          </div>
        </PopoverButton>
        <Transition
          enter="transition ease-out duration-75"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <PopoverPanel
            className={cn(
              'absolute right-[0px] z-10 mt-3 origin-top-right [--anchor-gap:var(--spacing-1)]',
              'w-80 rounded-md bg-white p-4 pt-3 shadow-custom'
            )}
          >
            <CloseButton className="absolute right-1 top-0 z-[1000]">
              <Button
                size="small"
                variant="icon"
                color="neutral"
                className="!w-8 rounded-full"
                startAdornment={<CloseIcon className="scale-[.9]" />}
              />
            </CloseButton>
            <div>
              <div className="mb-1 flex flex-1 items-center justify-between">
                <Label className="m-0">{t('labels.sort_items')}</Label>
              </div>
              <div className="flex gap-2">
                <ControlledInput
                  control={control}
                  type="select"
                  name={KeysEnum.SortAttribute}
                  classNameParent="w-full !m-0"
                  options={optionsSortAttribute}
                  defaultOption={optionsSortAttribute.at(3)}
                />
                <ControlledInput
                  control={control}
                  type="select"
                  name={KeysEnum.SortOrder}
                  classNameParent="w-full !m-0"
                  options={optionsSortOrder}
                  defaultOption={optionsSortOrder.at(1)}
                />
              </div>
            </div>
            <hr className="my-2 border !border-t-0 border-tertiary-600" />
            <div>
              <div className="flex flex-1 justify-end">
                <Button
                  variant="text"
                  size="small"
                  className="!p-0 !text-xs"
                  label={t('buttons.reset')}
                  onClick={handleClear}
                />
              </div>
            </div>
          </PopoverPanel>
        </Transition>
      </Popover>
    </div>
  );
}
